import { NgInject } from '@df/ng/ng-inject';
import { NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { ICmsBlock, NICmsBlock } from '@icms/core/resource/block/icms-block';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { NICmsContentComponent } from '../../interfaces/icms-content-component.interfaces';
import { ICmsProviderStorage } from './icms-provider-abstract';
import { ICmsProviderDefault } from './icms-provider-default';

export interface ICmsProviderBlockStorage<T> extends ICmsProviderStorage<T> {
    block?: ICmsBlock<NICmsBlock.IData<T>>;
    blockPromise?: Promise<ICmsBlock | void>;
    lastHash?: string;
    // component mode is when content is passed directly (no block is loaded)
    isComponentMode?: boolean;
}

export class ICmsProviderBlockContent<T> extends ICmsProviderDefault<T, ICmsProviderBlockStorage<T>> {
    @NgInject(ICmsBlockResource)
    private iCmsBlockResource!: ICmsBlockResource;

    protected override onChange(event: NICmsDataChange.IChangeDataEvent, instance: NICmsContentComponent.IComponent): void {
        if (this.isComponentMode(instance)) {
            super.onChange(event, instance);
        } else {
            if (instance && event.model === this.getStorage(instance).block) {
                if (event.valuePath.length < 3) {
                    this.updateData(instance);
                    // no cd here, its in parent class
                } else {
                    instance.detectChanges({ [this.propertyKey]: new NICmsContentComponent.Mutation(event) });
                }
            }
        }
    }

    protected override ngInit(instance: NICmsContentComponent.IComponent) {
        if (!this.isComponentMode(instance)) {
            this.reloadBlock(instance);
        }
        super.ngInit(instance);
    }

    protected override ngChanges(instance: NICmsContentComponent.IComponent) {
        if (this.isComponentMode(instance)) {
            super.ngChanges(instance);
        } else {
            this.reloadBlock(instance);
        }
    }

    private reloadBlock(instance: NICmsContentComponent.IComponent): T | void {
        const storage = this.getStorage(instance);
        // support for block content forced as component data (when using block as component)
        const meta = this.getMeta();
        const bareUrl = meta!.block!.url;
        const variant = instance.variant && (typeof instance.variant === 'string' ? instance.variant : instance.variant.id);
        const hash = `${bareUrl}@${variant}`;
        if (storage.lastHash === hash) {
            return storage.block?.data?.content;
        }
        storage.lastHash = hash;
        storage.block = undefined;

        // try sync
        storage.block = this.iCmsBlockResource.retrieveExisting({ bareUrl, variant });
        const componentData = storage.block?.data?.content;
        if (componentData) {
            this.updateData(instance);
            return;
        }

        // run async
        storage.blockPromise = this.iCmsBlockResource.retrieve({ bareUrl, variant, createOnError: true }, undefined);

        storage.blockPromise.then(loadedBlock => {
            if (loadedBlock) {
                storage.block = loadedBlock;
            }
            this.updateData(instance);
        });
    }

    private isComponentMode(instance: NICmsContentComponent.IComponent): boolean {
        const storage = this.getStorage(instance);
        if (storage.isComponentMode === undefined && !instance.initiated) {
            storage.isComponentMode = !!instance[this.propertyKey];
        }
        return storage.isComponentMode || false;
    }

    private updateData(instance: NICmsContentComponent.IComponent) {
        const data = this.getStorage(instance).block?.data?.content;
        this.setContent(data, instance);
    }
}
